"use client";

import { useEffect, useRef } from "react";

import { Route } from "@/enums/route";
import { useInactivityEnabled } from "@/hooks/useInactivityEnabled";
import { useLogout } from "@/hooks/useLogout";
import { logError } from "@/lib/logError";

/** localStorage key holding the epoch-ms time of the last crash logout. */
export const CRASH_LOGOUT_STORAGE_KEY = "inactivity.crashLogoutAt";

/**
 * After a crash logout, a second crash inside this window does not log out
 * again. Long enough to cover a sign-in round trip back to the dashboard.
 */
export const CRASH_LOGOUT_LOOP_GUARD_MS = 10 * 60_000;

/**
 * Whether a crash logout already happened inside the loop-guard window.
 * Unreadable storage counts as "no": failing closed means logging out.
 */
function hasRecentCrashLogout(now: number): boolean {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(CRASH_LOGOUT_STORAGE_KEY);
  } catch (error) {
    logError(
      "[InactivityCrashLogout] Could not read the crash logout time; logging out without the loop guard",
      error
    );
    return false;
  }
  if (raw === null) return false;
  const loggedOutAt = Number(raw);
  if (!Number.isFinite(loggedOutAt)) return false;
  const age = now - loggedOutAt;
  return age >= 0 && age < CRASH_LOGOUT_LOOP_GUARD_MS;
}

function recordCrashLogout(now: number): void {
  try {
    window.localStorage.setItem(CRASH_LOGOUT_STORAGE_KEY, String(now));
  } catch (error) {
    logError(
      "[InactivityCrashLogout] Could not record the crash logout time; the loop guard will not apply",
      error
    );
  }
}

/**
 * Error-boundary fallback for `InactivityController` in the protected layout.
 *
 * If the inactivity controller crashes, the idle timer is gone and the session
 * would otherwise stay open indefinitely on an unattended device. This fails
 * closed: it signs the user out once, through the same `useLogout` path and
 * options the idle timeout uses, and renders nothing.
 *
 * Gate: it logs out only when the idle timer itself would be running — it
 * reads `enabled` from `useInactivityEnabled`, the same hook `useIdleLogout`
 * drives the timer from (kill switch, test env and its E2E override,
 * authenticated session). When idle logout is off there is no timer to lose,
 * so a crash logs nobody out.
 *
 * Loop guard: a crash that happens on every render would otherwise loop —
 * logout, sign in, dashboard, same crash, logout — and spend the auto sign-in
 * budget. The logout time is kept in localStorage; a second crash inside
 * `CRASH_LOGOUT_LOOP_GUARD_MS` does not log out again but reports the repeat
 * through `logError` (Sentry + Mixpanel). Storage that cannot be read still
 * logs out, so the guard can only ever skip a second logout, never the first.
 *
 * The ref guard keeps it to one decision per mount: re-renders, or a new
 * `logout` identity from `useLogout`, never start a second sign-out.
 */
export function InactivityCrashLogout() {
  const { enabled } = useInactivityEnabled();
  const logout = useLogout();
  const hasHandledCrashRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasHandledCrashRef.current) return;
    hasHandledCrashRef.current = true;

    const now = Date.now();
    if (hasRecentCrashLogout(now)) {
      logError(
        "[InactivityCrashLogout] Inactivity controller crashed again within the loop-guard window; not logging out again",
        new Error("Repeat inactivity controller crash after a crash logout"),
        { loopGuardMs: CRASH_LOGOUT_LOOP_GUARD_MS }
      );
      return;
    }

    recordCrashLogout(now);
    logout({ redirectTo: Route.Dashboard, reason: "inactivity" });
  }, [enabled, logout]);

  return null;
}
